import React from "react";
import type { ReactNode } from "react";
import type { User } from "@auth0/auth0-spa-js";
import env from "@beam-australia/react-env";
import { Auth0Context } from "utils/react-auth0-wrapper";
import type { Auth0ContextValue } from "utils/react-auth0-wrapper";
import { getPath } from "store/selectors/auth0";

// E2E-only fake Auth0 provider, selected in index.tsx when
// REACT_APP_E2E_AUTH_STUB=true (set by .env.e2e for the Playwright suite —
// never in a deployed env). It supplies the same Auth0Context the real
// Auth0Provider does, so AuthApolloProvider / App / PrivateRoute run
// unmodified while MSW answers the GraphQL layer. The stub token is
// client-side-only theater: it grants nothing against a real backend.

declare global {
  interface Window {
    __e2eLoginWithRedirectCalls?: unknown[];
  }
}

export const e2eAuthStubEnabled = () => env("E2E_AUTH_STUB") === "true";

// AuthApolloProvider decodes the JWT payload to schedule a refresh at
// exp - 10s, so the token must be structurally valid with a future `exp`.
// Keep exp ~1h out: a far-future exp overflows setTimeout's 32-bit delay,
// which fires immediately and loops the refresh.
const makeStubToken = () => {
  const encode = (value: object) => btoa(JSON.stringify(value));
  const header = encode({ alg: "none", typ: "JWT" });
  const payload = encode({ exp: Math.floor(Date.now() / 1000) + 60 * 60 });
  return `${header}.${payload}.e2e-stub-signature`;
};

// Specs that need extra namespaced claims (e.g. active_account_id for
// multi-account flows) seed them before boot via
// page.addInitScript → localStorage "e2eAuthClaimOverrides", a JSON object of
// un-namespaced claim keys. Read per makeStubUser call so a reload picks up
// changes; inert when the key is absent.
const readClaimOverrides = (): Record<string, unknown> => {
  try {
    const raw = window.localStorage.getItem("e2eAuthClaimOverrides");
    if (!raw) return {};
    const overrides = JSON.parse(raw) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(overrides).map(([key, value]) => [
        `${getPath()}/${key}`,
        value,
      ])
    );
  } catch {
    return {};
  }
};

// Custom claims are namespaced by the Auth0 domain (store/selectors/auth0).
// icon_id must be non-empty or App.tsx skips the GetState bootstrap on the
// individual site; the roles claim drives the admin portal's sidebar/route
// permissions (store/selectors/admin.ts) and is inert on the other portals.
const makeStubUser = (): User => ({
  sub: "auth0|e2e-stub-user",
  email: "e2e@iconsavingsplan.com",
  email_verified: true,
  [`${getPath()}/icon_id`]: "e2e-icon-id",
  [`${getPath()}/roles`]: ["Icon Super Admin"],
  ...readClaimOverrides(),
});

// Auth0 redirects leave the app, so specs can't follow them. Record the calls
// instead: a spec that triggers a partner re-auth (connection / acr_values)
// asserts against window.__e2eLoginWithRedirectCalls.
const recordLoginWithRedirect = (options?: unknown) => {
  const calls = (window.__e2eLoginWithRedirectCalls ??= []);
  calls.push(options);
  return Promise.resolve();
};

interface E2eStubAuth0ProviderProps {
  children?: ReactNode;
  // accept-and-ignore the real Auth0Provider's props (domain, clientId, …)
  // so index.tsx can swap providers without branching its JSX
  [key: string]: unknown;
}

export const E2eStubAuth0Provider = ({
  children,
}: E2eStubAuth0ProviderProps) => {
  const value: Auth0ContextValue = {
    isAuthenticated: true,
    user: makeStubUser(),
    loading: false,
    appState: undefined,
    handleRedirectCallback: () => Promise.resolve(),
    getUser: () => Promise.resolve(makeStubUser()),
    getIdTokenClaims: () => Promise.resolve(undefined),
    loginWithRedirect: recordLoginWithRedirect,
    loginWithPopup: () => Promise.resolve(),
    getTokenSilently: () => Promise.resolve(makeStubToken()),
    getTokenWithPopup: () => Promise.resolve(undefined),
    logout: () => {},
  };

  return (
    <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
  );
};
